import React, { useState, useEffect } from 'react';
import { TextBox, CheckBox, Button } from 'devextreme-react';
import { Popup } from 'devextreme-react/popup';
import TagBox from 'devextreme-react/tag-box';
import axios from 'axios';
import data from '../assets/apiRoutes.json';
import { MESSAGES } from '../common/Messages';

const toRoleList = (value) => (Array.isArray(value) ? value : []);

const EditFieldPopup = ({ field, visible, onClose, handleFieldUpdate }) => {
  const [label, setLabel] = useState('');
  const [roles, setRoles] = useState([]);
  const [visibleRole, setVisibleRole] = useState([]);
  const [editRole, setEditRole] = useState([]);
  const [required, setRequired] = useState(false);

  // Roles come from the auth server, the same list shown on the Security > User Security "Roles" grid,
  // where "key" holds the role name. Only the names are kept: the role lists store names, and a plain
  // string list also lets the tag boxes display saved roles without waiting for this request.
  useEffect(() => {
    axios
      .get(`${window._env_.REACT_APP_AUTH_URL}/${data.idp.globalRoles}`)
      .then((res) => setRoles((res.data || []).map((r) => r.key).filter(Boolean)))
      .catch((err) => console.log(err, MESSAGES.FETCH_ERROR));
  }, []);

  useEffect(() => {
    if (field) {
      setLabel(field.label);
      // Layouts saved before Visible and Editable had separate role lists carry a single "role"
      // list, which gated visibility - fall back to it so those layouts keep working.
      setVisibleRole(toRoleList(field.visibleRole || field.role));
      setEditRole(toRoleList(field.editRole));
      setRequired(field.required || false);
    }
  }, [field]);

  const handleSave = () => {
    handleFieldUpdate({
      ...field,
      label,
      required,
      visibleRole,
      // Kept in step with visibleRole so anything still reading the legacy property agrees.
      role: visibleRole,
      editRole,
    });
    onClose();
  };

  return (
    <Popup
      visible={visible}
      onHiding={onClose}
      dragEnabled={true}
      hideOnOutsideClick={true}
      showCloseButton={true}
      title="Edit Field"
      width={420}
      height="auto"
    >
      <div>
        <fieldset className="col-fieldset px-2 pb-2 mb-3">
          <legend>Field</legend>
          <div className="mb-3">
            <TextBox
              label="Label"
              labelMode="static"
              stylingMode="outlined"
              value={label}
              onValueChanged={(e) => setLabel(e.value)}
            />
          </div>
          <div className="d-flex flex-wrap align-items-center pb-1">
            <CheckBox
              text="Required"
              value={required}
              onValueChanged={(e) => setRequired(e.value)}
            />
          </div>
        </fieldset>

        <fieldset className="col-fieldset px-2 pb-2 mb-3">
          <legend>Visible</legend>
          <div className="mb-2">
            <TagBox
              label="Visible Role"
              labelMode="static"
              stylingMode="outlined"
              items={roles}
              value={visibleRole}
              placeholder="Select roles..."
              showClearButton={true}
              searchEnabled={true}
              showSelectionControls={true}
              onValueChanged={(e) => setVisibleRole(toRoleList(e.value))}
              multiline={true}
            />
          </div>
          <div className="dx-field-item-help-text pb-1">
            Leave empty to make this field visible to all roles.
          </div>
        </fieldset>

        <fieldset className="col-fieldset px-2 pb-2 mb-2">
          <legend>Editable</legend>
          <div className="mb-2">
            <TagBox
              label="Edit Role"
              labelMode="static"
              stylingMode="outlined"
              items={roles}
              value={editRole}
              placeholder="Select roles..."
              showClearButton={true}
              searchEnabled={true}
              showSelectionControls={true}
              onValueChanged={(e) => setEditRole(toRoleList(e.value))}
              multiline={true}
            />
          </div>
          <div className="dx-field-item-help-text pb-1">
            Leave empty to let all roles edit this field.
          </div>
        </fieldset>

        <div className="d-flex justify-content-end pt-3">
          <Button text="Save" type="default" onClick={handleSave} />
        </div>
      </div>
    </Popup>
  );
};

export default EditFieldPopup;
